/**
 * ACCOMMODATIONS registry — the accommodation vocabulary (CP-R1312 canon:
 * ONE vocabulary, no lane-specific parallel stores).
 * Loads schema/accommodations.json (single canonical source per ADR-0017),
 * Zod-validates at module load, FAIL CLOSED.
 *
 * NOTE: the extraction from the legacy library is PARTIAL (17 of the legacy 53) —
 * recorded in schema/registry.contracts.json (extractionComplete:false). The floor
 * ratchets up as extraction completes; this loader validates shape and code
 * resolution, and refuses contraindication data it cannot resolve (CP-R1306:
 * an unresolvable contraindication is how read-aloud reached deaf students).
 */
import { z } from 'zod'
import { AppError } from '../errors/app-error'
import accommodationsJson from '../../../../schema/accommodations.json'

export const AccommodationEntrySchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    category: z.string().min(1),
    eligibleDisabilities: z.array(z.string().min(1)),
    contraindicatedFor: z.array(z.string().min(1)).optional(),
    suggestedFor: z.array(z.string().min(1)).optional(),
    stateAssessmentEligible: z.union([z.boolean(), z.record(z.boolean())]),
    ospi_id: z.string().min(1).optional(),
    nursingOnly: z.boolean().optional(),
    waStatutoryDiabetes: z.boolean().optional(),
    specialConsiderationFactors: z.array(z.string().min(1)).optional(),
    subdomains: z.array(z.string().min(1)).optional(),
  })
  .strict()

export type AccommodationEntry = z.infer<typeof AccommodationEntrySchema>

const AccommodationsFileSchema = z
  .object({
    description: z.string(),
    version: z.string(),
    target: z.string(),
    roadmapNote: z.string(),
    categories: z.record(z.string()),
    accommodations: z.array(AccommodationEntrySchema).min(10),
    disabilityCodes: z.record(z.string()),
    specialConsiderationFactors: z.record(z.string()),
  })
  .strict()

interface LoadedAccommodations {
  accommodations: readonly AccommodationEntry[]
  disabilityCodes: ReadonlyMap<string, string>
  categories: ReadonlyMap<string, string>
}

function loadAccommodations(): LoadedAccommodations {
  const parsed = AccommodationsFileSchema.safeParse(accommodationsJson)
  if (!parsed.success) {
    throw AppError.validation(
      'ACCOMMODATIONS registry failed validation — refusing to load a malformed vocabulary (fail closed)',
      parsed.error.issues,
    )
  }
  const categories = new Map(Object.entries(parsed.data.categories))
  const codes = new Map(Object.entries(parsed.data.disabilityCodes))
  const factors = new Set(Object.keys(parsed.data.specialConsiderationFactors))

  const seen = new Set<string>()
  for (const a of parsed.data.accommodations) {
    if (seen.has(a.id)) {
      throw AppError.validation(`ACCOMMODATIONS registry: duplicate accommodation id '${a.id}'`)
    }
    seen.add(a.id)
    if (!categories.has(a.category)) {
      throw AppError.validation(`ACCOMMODATIONS registry: '${a.id}' has undeclared category '${a.category}'`)
    }
    for (const code of a.eligibleDisabilities) {
      if (!codes.has(code)) {
        throw AppError.validation(`ACCOMMODATIONS registry: '${a.id}' names unknown disability code '${code}' in eligibleDisabilities`)
      }
    }
    for (const code of a.contraindicatedFor ?? []) {
      if (!codes.has(code)) {
        throw AppError.validation(
          `ACCOMMODATIONS registry: '${a.id}' names unknown disability code '${code}' in contraindicatedFor — an unresolvable contraindication CANNOT SUPPRESS (the CP-R1306 read-aloud-to-deaf-students class)`,
        )
      }
    }
    for (const f of a.specialConsiderationFactors ?? []) {
      if (!factors.has(f)) {
        throw AppError.validation(`ACCOMMODATIONS registry: '${a.id}' names undeclared special-consideration factor '${f}'`)
      }
    }
  }
  return {
    accommodations: Object.freeze(parsed.data.accommodations),
    disabilityCodes: codes,
    categories,
  }
}

const loaded = loadAccommodations()

/** Every accommodation, validated. Frozen. */
export const ACCOMMODATIONS: readonly AccommodationEntry[] = loaded.accommodations

/** The disability-code vocabulary the accommodation data is keyed to. */
export const ACCOMMODATION_DISABILITY_CODES: ReadonlyMap<string, string> = loaded.disabilityCodes

const ACCOMMODATION_INDEX: ReadonlyMap<string, AccommodationEntry> = new Map(
  ACCOMMODATIONS.map((a) => [a.id, a]),
)

export function hasAccommodation(id: string): boolean {
  return ACCOMMODATION_INDEX.has(id)
}

/** Resolve an accommodation by id. THROWS on unknown id — one vocabulary, no parallel stores. */
export function getAccommodation(id: string): AccommodationEntry {
  const a = ACCOMMODATION_INDEX.get(id)
  if (!a) {
    throw AppError.notFound(
      `Unknown accommodation id '${id}' — accommodations resolve from the ACCOMMODATIONS registry only (no lane-local vocabularies)`,
    )
  }
  return a
}

/**
 * Whether an accommodation is contraindicated for a disability code.
 * THROWS on an unknown disability code — deciding 'not contraindicated' for a
 * code the vocabulary cannot resolve is the CP-R1306 failure mode (fail closed).
 */
export function isContraindicated(accommodationId: string, disabilityCode: string): boolean {
  if (!loaded.disabilityCodes.has(disabilityCode)) {
    throw AppError.validation(
      `Unknown disability code '${disabilityCode}' — cannot evaluate contraindication for a code outside the vocabulary (fail closed; CP-R1306)`,
    )
  }
  const a = getAccommodation(accommodationId)
  return (a.contraindicatedFor ?? []).includes(disabilityCode)
}
